import { useEffect, useRef, useState } from 'react';

import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';

import { getIntlLocale } from '@/shared/i18n';
import type { NormalizedApiError } from '@/shared/api';
import {
  FeedbackSnackbar,
  SettingsScaffold,
  useDomainPermissions,
  useFeedback,
  useSettingsForm,
} from '@/features/store-settings';

import {
  useChatbotAvailability,
  useChatbotChannelSettings,
  useChatbotDictionaries,
  useChatbotFaqIntents,
  useChatbotSettings,
  useContentLanguages,
  useDeleteChatbotChannelSettings,
  useEffectiveChatbotSettings,
  useUpdateChatbotChannelSettings,
  useUpdateChatbotSettings,
} from '../../hooks';
import { createChannelOverrideController } from '../../model/channelOverrideController';
import type { ChannelForm } from '../../model/channelOverrideController';
import {
  BEHAVIOUR_CARD_FIELDS,
  ESCALATION_CARD_FIELDS,
  buildGeneralUpdate,
  normalizeEnabledCommands,
} from '../../model/formModel';
import type {
  ChatbotChannel,
  ChatbotSettingsFields,
  ChatbotSettingsOverrides,
  UpdateChatbotChannelSettingsRequest,
} from '../../model/types';
import {
  AiProviderCard,
  AvailabilityStrip,
  BehaviourCard,
  EscalationAiCard,
  RequiredTextsCard,
} from '../components';
import type { ChannelScope } from '../components';

const SCOPE_CARD_KEYS = ['behaviour', 'escalation'] as const;
type ScopeCardKey = (typeof SCOPE_CARD_KEYS)[number];

/**
 * A stable (module-level, never-recreated) empty overrides object. `useSettingsForm`'s reseed
 * effect keys off `loaded` by reference — feeding it a fresh `{}` literal computed inline on every
 * render (e.g. `data?.overrides ?? {}`) never stabilises, so the effect fires every render and
 * clobbers any in-progress edit right back to empty on the very next commit (and, worse, loops:
 * each reseed is itself a new render). Channel overrides with no saved data (`data === null`, the
 * 404 "inherits general" case) must resolve to THIS reference, never a new literal.
 */
const EMPTY_OVERRIDES: ChatbotSettingsOverrides = {};

function parseScopeParam(raw: string | null, card: ScopeCardKey): ChannelScope {
  if (!raw) return 'GENERAL';
  for (const part of raw.split(',')) {
    const [key, value] = part.split(':');
    if (key === card && (value === 'TELEGRAM' || value === 'MAX' || value === 'GENERAL')) {
      return value;
    }
  }
  return 'GENERAL';
}

function normalizeOverridesForSave(
  overrides: ChatbotSettingsOverrides,
  alwaysOnCodes?: readonly string[],
): ChatbotSettingsOverrides {
  if (!('enabledCommands' in overrides) || !overrides.enabledCommands) return overrides;
  return {
    ...overrides,
    enabledCommands: normalizeEnabledCommands(overrides.enabledCommands, alwaysOnCodes),
  };
}

/**
 * `/settings/chatbot` — behaviour, escalation & AI, per-channel overrides, availability, the
 * required bot texts card and the AI provider card (design-chatbot-settings-page.md incl. §17,
 * design-chatbot-ai-provider-card.md).
 */
export function ChatbotSettingsPage() {
  const { t } = useTranslation('chatbot');
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { canView, canManage } = useDomainPermissions('chatbot');
  const channelsPermissions = useDomainPermissions('channels');
  const telegramPermissions = useDomainPermissions('telegram');
  const maxPermissions = useDomainPermissions('max');
  const tenantPermissions = useDomainPermissions('tenant');
  const templatesPermissions = useDomainPermissions('chatbot-templates');

  const generalQuery = useChatbotSettings();
  const availabilityQuery = useChatbotAvailability();
  const dictionariesQuery = useChatbotDictionaries();
  const telegramChannelQuery = useChatbotChannelSettings('TELEGRAM');
  const maxChannelQuery = useChatbotChannelSettings('MAX');
  const telegramEffectiveQuery = useEffectiveChatbotSettings('TELEGRAM');
  const maxEffectiveQuery = useEffectiveChatbotSettings('MAX');
  const languagesQuery = useContentLanguages();
  const defaultLocale =
    languagesQuery.data?.find((l) => l.defaultLocale)?.locale ?? languagesQuery.data?.[0]?.locale;
  const faqQuery = useChatbotFaqIntents(defaultLocale);

  const updateGeneral = useUpdateChatbotSettings();
  const updateTelegramChannel = useUpdateChatbotChannelSettings('TELEGRAM');
  const updateMaxChannel = useUpdateChatbotChannelSettings('MAX');
  const deleteTelegramChannel = useDeleteChatbotChannelSettings('TELEGRAM');
  const deleteMaxChannel = useDeleteChatbotChannelSettings('MAX');

  const generalForm = useSettingsForm(generalQuery.data);
  const telegramOverridesLoaded = telegramChannelQuery.isSuccess
    ? (telegramChannelQuery.data?.overrides ?? EMPTY_OVERRIDES)
    : undefined;
  const maxOverridesLoaded = maxChannelQuery.isSuccess
    ? (maxChannelQuery.data?.overrides ?? EMPTY_OVERRIDES)
    : undefined;
  const telegramForm = useSettingsForm<ChatbotSettingsOverrides>(telegramOverridesLoaded);
  const maxForm = useSettingsForm<ChatbotSettingsOverrides>(maxOverridesLoaded);

  const { feedback, success, error: notifyError, clear } = useFeedback();

  const [behaviourValid, setBehaviourValid] = useState(true);
  const [escalationValid, setEscalationValid] = useState(true);
  const [resetAllOpen, setResetAllOpen] = useState(false);
  const [pendingResets, setPendingResets] = useState<Set<keyof ChatbotSettingsFields>>(new Set());

  const aiSectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (location.hash === '#ai' && aiSectionRef.current) {
      aiSectionRef.current.scrollIntoView({ block: 'start' });
      aiSectionRef.current.focus();
    }
  }, [location.hash]);

  const behaviourScope = parseScopeParam(searchParams.get('scope'), 'behaviour');
  const escalationScope = parseScopeParam(searchParams.get('scope'), 'escalation');

  const setScope = (card: ScopeCardKey, value: ChannelScope) => {
    const next: Record<ScopeCardKey, ChannelScope> = {
      behaviour: card === 'behaviour' ? value : behaviourScope,
      escalation: card === 'escalation' ? value : escalationScope,
    };
    const serialized = SCOPE_CARD_KEYS.filter((key) => next[key] !== 'GENERAL')
      .map((key) => `${key}:${next[key]}`)
      .join(',');
    const nextParams = new URLSearchParams(searchParams);
    if (serialized) nextParams.set('scope', serialized);
    else nextParams.delete('scope');
    setSearchParams(nextParams, { replace: true });
  };

  const onChangeAiMode = (scope: ChatbotChannel | 'GENERAL') => {
    setScope('escalation', scope);
    document.getElementById('escalation')?.scrollIntoView({ block: 'start' });
  };

  // ── Pending-reset model (design §17.1.4) ────────────────────────────────────────
  const onUseDefault = (field: keyof ChatbotSettingsFields) => {
    if (!generalQuery.data) return;
    generalForm.setField(field, generalQuery.data[field]);
    const wasDefaulted = generalQuery.data.defaultedFields?.includes(field as string) ?? false;
    setPendingResets((current) => {
      const next = new Set(current);
      if (wasDefaulted) next.delete(field);
      else next.add(field);
      return next;
    });
  };
  const onKeepYours = (field: keyof ChatbotSettingsFields) => {
    if (!generalQuery.data) return;
    generalForm.setField(field, generalQuery.data[field]);
    setPendingResets((current) => {
      const next = new Set(current);
      next.delete(field);
      return next;
    });
  };
  // Editing a field directly always cancels any pending reset for it (§17.1.4's "Edit field f" row).
  // `ChatbotGeneralSettings extends ChatbotSettingsFields` — every field this touches has an
  // identical type on both interfaces; the cast only widens the generic `K` domain `setField`
  // accepts, it does not change what value ends up stored (ported from the pre-existing pattern).
  const setGeneralField = <K extends keyof ChatbotSettingsFields>(
    key: K,
    value: ChatbotSettingsFields[K],
  ) => {
    generalForm.setField(key, value as never);
    if (pendingResets.has(key)) {
      setPendingResets((current) => {
        const next = new Set(current);
        next.delete(key);
        return next;
      });
    }
  };

  const dirty = generalForm.dirty || pendingResets.size > 0 || telegramForm.dirty || maxForm.dirty;
  const saving =
    updateGeneral.isPending ||
    updateTelegramChannel.isPending ||
    updateMaxChannel.isPending ||
    deleteTelegramChannel.isPending ||
    deleteMaxChannel.isPending;

  const unsavedScopeLabels = [
    generalForm.dirty || pendingResets.size > 0 ? t('scope.general') : null,
    telegramForm.dirty ? t('scope.telegram') : null,
    maxForm.dirty ? t('scope.max') : null,
  ].filter((label): label is string => label !== null);

  const alwaysOnCommandCodes = dictionariesQuery.data?.commands
    .filter((c) => c.alwaysOn)
    .map((c) => c.code);

  function saveGeneral(): Promise<void> {
    return new Promise((resolve, reject) => {
      if ((!generalForm.dirty && pendingResets.size === 0) || !generalForm.draft) {
        resolve();
        return;
      }
      const body = {
        ...buildGeneralUpdate(
          generalForm.draft,
          generalQuery.data,
          pendingResets,
          alwaysOnCommandCodes,
        ),
        version: generalForm.draft.version,
      };
      updateGeneral.mutate(body, {
        onSuccess: () => {
          setPendingResets(new Set());
          resolve();
        },
        onError: (error: NormalizedApiError) => {
          notifyError(error.message);
          if (error.status === 409) void generalQuery.refetch();
          reject(error);
        },
      });
    });
  }

  function saveChannel(channel: ChatbotChannel): Promise<void> {
    return new Promise((resolve, reject) => {
      const form = channel === 'TELEGRAM' ? telegramForm : maxForm;
      const channelQuery = channel === 'TELEGRAM' ? telegramChannelQuery : maxChannelQuery;
      const updateMutation = channel === 'TELEGRAM' ? updateTelegramChannel : updateMaxChannel;
      const deleteMutation = channel === 'TELEGRAM' ? deleteTelegramChannel : deleteMaxChannel;
      if (!form.dirty || !form.draft) {
        resolve();
        return;
      }
      const onError = (error: NormalizedApiError) => {
        notifyError(error.message);
        if (error.status === 409) void channelQuery.refetch();
        reject(error);
      };
      if (Object.keys(form.draft).length === 0) {
        deleteMutation.mutate(undefined, { onSuccess: () => resolve(), onError });
        return;
      }
      const body: UpdateChatbotChannelSettingsRequest = {
        overrides: normalizeOverridesForSave(form.draft, alwaysOnCommandCodes),
        version: channelQuery.data?.version ?? 0,
      };
      updateMutation.mutate(body, { onSuccess: () => resolve(), onError });
    });
  }

  const handleSave = () => {
    if (!dirty) return;
    void (async () => {
      try {
        if (generalForm.dirty || pendingResets.size > 0) await saveGeneral();
        if (telegramForm.dirty) await saveChannel('TELEGRAM');
        if (maxForm.dirty) await saveChannel('MAX');
        success(t('settings.saved'));
      } catch {
        // Already surfaced via `notifyError` inside each step.
      }
    })();
  };

  const dirtyScopeCount = [
    generalForm.dirty || pendingResets.size > 0,
    telegramForm.dirty,
    maxForm.dirty,
  ].filter(Boolean).length;
  const resetAll = () => {
    generalForm.reset();
    setPendingResets(new Set());
    telegramForm.reset();
    maxForm.reset();
  };
  const handleReset = () => {
    if (dirtyScopeCount > 1) setResetAllOpen(true);
    else resetAll();
  };

  const languageRegistrySummary = languagesQuery.data
    ?.map((language) => (language.defaultLocale ? `${language.name} (default)` : language.name))
    .join(', ');

  const channelForms: Record<ChatbotChannel, ChannelForm> = {
    TELEGRAM: telegramForm,
    MAX: maxForm,
  };
  const channelBaselines: Record<ChatbotChannel, ChatbotSettingsOverrides> = {
    TELEGRAM: telegramChannelQuery.data?.overrides ?? EMPTY_OVERRIDES,
    MAX: maxChannelQuery.data?.overrides ?? EMPTY_OVERRIDES,
  };
  const effective = {
    TELEGRAM: telegramEffectiveQuery.data,
    MAX: maxEffectiveQuery.data,
  };
  const effectiveAiMode = {
    TELEGRAM: telegramEffectiveQuery.data?.aiMode,
    MAX: maxEffectiveQuery.data?.aiMode,
  };
  const effectiveOverriddenFields = {
    TELEGRAM: telegramEffectiveQuery.data?.overriddenFields,
    MAX: maxEffectiveQuery.data?.overriddenFields,
  };

  const showPlatformUpdateNotice = Boolean(generalQuery.data?.updatedBy?.startsWith('system:'));

  return (
    <SettingsScaffold
      title={t('settings.title')}
      subtitle={t('settings.subtitle')}
      icon={<SmartToyRoundedIcon />}
      query={generalQuery}
      canView={canView}
      canManage={canManage}
      audit={generalQuery.data}
      dirty={dirty}
      saving={saving}
      saveDisabled={!behaviourValid || !escalationValid}
      dirtyMessage={
        unsavedScopeLabels.length > 0
          ? t('settings.unsavedScopes', { scopes: unsavedScopeLabels.join(', ') })
          : undefined
      }
      onSave={handleSave}
      onReset={handleReset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {(general) => {
        const generalDraft = generalForm.draft;
        if (!generalDraft) return null;

        const behaviourController = createChannelOverrideController({
          scope: behaviourScope,
          cardFields: BEHAVIOUR_CARD_FIELDS,
          generalDraft,
          generalLoaded: generalQuery.data,
          setGeneralField,
          pendingResets,
          onUseDefault,
          onKeepYours,
          channelForms,
          channelBaselines,
          effective,
          canManage,
        });
        const escalationController = createChannelOverrideController({
          scope: escalationScope,
          cardFields: ESCALATION_CARD_FIELDS,
          generalDraft,
          generalLoaded: generalQuery.data,
          setGeneralField,
          pendingResets,
          onUseDefault,
          onKeepYours,
          channelForms,
          channelBaselines,
          effective,
          canManage,
          lockedFields: {
            aiMode:
              availabilityQuery.data?.aiCapability?.effective === false &&
              availabilityQuery.data.aiCapability.lockedByPlatform === true,
            handoffEnabled:
              availabilityQuery.data?.humanHandoffCapability?.effective === false &&
              availabilityQuery.data.humanHandoffCapability.lockedByPlatform === true,
          },
        });

        return (
          <Stack spacing={2.5}>
            {showPlatformUpdateNotice ? (
              <Alert severity="info">
                {t('settings.systemUpdateNotice', {
                  date: new Intl.DateTimeFormat(getIntlLocale(), { dateStyle: 'medium' }).format(
                    new Date(generalQuery.data!.updatedAt ?? Date.now()),
                  ),
                })}
              </Alert>
            ) : null}
            {general.version === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('settings.defaultsNotice')}
              </Typography>
            ) : null}

            <AvailabilityStrip
              query={availabilityQuery}
              canViewChannels={channelsPermissions.canView}
              canViewTelegram={telegramPermissions.canView}
              canViewMax={maxPermissions.canView}
              canViewTenant={tenantPermissions.canView}
            />

            <RequiredTextsCard
              query={availabilityQuery}
              canViewTemplates={templatesPermissions.canView}
              languages={languagesQuery.data}
            />

            <BehaviourCard
              scope={behaviourScope}
              onScopeChange={(scope) => setScope('behaviour', scope)}
              controller={behaviourController}
              canManage={canManage}
              languageRegistrySummary={languageRegistrySummary}
              onValidityChange={setBehaviourValid}
              dictionaries={dictionariesQuery.data}
              dictionariesLoading={dictionariesQuery.isLoading}
              dictionariesError={dictionariesQuery.isError}
              onRetryDictionaries={() => void dictionariesQuery.refetch()}
            />

            <EscalationAiCard
              scope={escalationScope}
              onScopeChange={(scope) => setScope('escalation', scope)}
              controller={escalationController}
              canManage={canManage}
              aiCapability={availabilityQuery.data?.aiCapability}
              handoffCapability={availabilityQuery.data?.humanHandoffCapability}
              canViewTenant={tenantPermissions.canView}
              onValidityChange={setEscalationValid}
              dictionaries={dictionariesQuery.data}
              faqIntents={faqQuery.data?.items}
              faqLoading={faqQuery.isLoading}
              faqError={faqQuery.isError}
            />

            <Box
              id="ai"
              ref={aiSectionRef}
              tabIndex={-1}
              aria-label={t('ai.title')}
              sx={{ outline: 'none' }}
            >
              <AiProviderCard
                aiCapability={availabilityQuery.data?.aiCapability}
                availabilityChannels={availabilityQuery.data?.channels ?? []}
                availabilityLoading={availabilityQuery.isLoading}
                availabilityError={availabilityQuery.isError}
                effectiveAiMode={effectiveAiMode}
                effectiveOverriddenFields={effectiveOverriddenFields}
                providers={dictionariesQuery.data?.providers}
                dictionariesLoading={dictionariesQuery.isLoading}
                dictionariesError={dictionariesQuery.isError}
                onRetryDictionaries={() => void dictionariesQuery.refetch()}
                canViewTenant={tenantPermissions.canView}
                onChangeAiMode={onChangeAiMode}
              />
            </Box>

            <Dialog open={resetAllOpen} onClose={() => setResetAllOpen(false)}>
              <DialogTitle>
                {t('settings.resetDialog.title', { count: dirtyScopeCount })}
              </DialogTitle>
              <DialogContent>
                <DialogContentText>{t('settings.resetDialog.body')}</DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setResetAllOpen(false)} autoFocus>
                  {t('common:action.cancel')}
                </Button>
                <Button
                  color="error"
                  onClick={() => {
                    resetAll();
                    setResetAllOpen(false);
                  }}
                >
                  {t('common:action.reset')}
                </Button>
              </DialogActions>
            </Dialog>
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
